import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SHOWCASE } from "@/lib/site-data";

export default function ConcreteShowcase() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="concrete" data-testid="concrete-showcase-section" className="bg-black py-28 md:py-36 overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal>
          <Overline>Concrete</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight max-w-2xl">
              Concrete That Looks As Good As It Performs.
            </h2>
            <p className="text-cyan-400/80 text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
              Scroll the gallery <ArrowRight size={16} />
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <div className="flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory px-4 md:px-8 pb-4">
          {SHOWCASE.map((item, i) => (
            <button
              key={item.title}
              data-testid={`showcase-card-${i}`}
              onClick={() => setLightbox(item)}
              className="group relative shrink-0 w-[78vw] sm:w-[400px] h-[480px] rounded-2xl overflow-hidden snap-start text-left transition-transform duration-500 hover:-translate-y-2"
            >
              <img
                src={item.img}
                alt={item.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">{item.tag}</span>
                <h3 className="font-display text-2xl font-semibold text-white mt-1">{item.title}</h3>
              </div>
              <span className="absolute top-5 right-5 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-[11px] font-bold tracking-widest text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                CLICK TO EXPAND
              </span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="showcase-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-black/92 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.img} alt={lightbox.title} className="w-full max-h-[75vh] object-cover rounded-2xl" />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">{lightbox.tag}</span>
                  <h3 className="font-display text-2xl font-semibold text-white">{lightbox.title}</h3>
                </div>
                <button
                  data-testid="showcase-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="rounded-full border border-white/20 p-3 text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors duration-200"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
